import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { AdminAvatar } from '@/components/admin/avatar';
import { AdminDonut } from '@/components/admin/donut';
import { AdminIcon } from '@/components/admin/icon';
import { AdminLineChart } from '@/components/admin/line-chart';
import { AdminSpark } from '@/components/admin/spark';
import { AlertBanner } from '@/components/admin/alert-banner';
import { AdminButton } from '@/components/admin/button';
import { Card, CardBody, CardHead } from '@/components/admin/card';
import { PageHead } from '@/components/admin/page-head';
import { Segmented } from '@/components/admin/segmented';
import { Tag } from '@/components/admin/tag';
import { useReportingHome } from '@/hooks/use-reporting';
import { accord, pluriel } from '@/lib/pluriel';
import { fmtFCFA } from '@/lib/format';
import type { HomeReport, HomeRevenuePoint } from '@/lib/reporting/home';
import { statusOrderV2 } from '@/lib/status';

const CATEGORY_ACCENT: Record<string, string> = {
  orders: 'info',
  payments: 'success',
  catalog: 'warning',
  customers: 'info',
  finance: 'success',
  logistics: 'info',
  auth: 'neutral',
  users: 'neutral',
};

const RANGE_DAYS = {
  '7j': 7,
  '14j': 14,
  '30j': 30,
  '90j': 90,
} as const;

const EMPTY_REPORT: HomeReport = {
  generatedAt: new Date().toISOString(),
  source: 'loading',
  issues: [],
  orders: {
    todayCount: 0,
    todayRevenue: 0,
    averageBasket: 0,
    inProgress: 0,
    slaLate: 0,
    byStatus: {},
  },
  latestOrders: [],
  activity: [],
  revenueSeries: [],
  paymentMix: [],
  finance: null,
  support: null,
};

const accentToColor = (a: string): string =>
  a === 'success'
    ? 'var(--a-success)'
    : a === 'warning'
      ? 'var(--a-warning)'
      : a === 'danger'
        ? 'var(--a-danger)'
        : a === 'info'
          ? 'var(--a-info)'
          : 'var(--a-border-strong)';

const auditAccent = (category: string, result: 'success' | 'failure'): string => {
  if (result === 'failure') return 'danger';
  return CATEGORY_ACCENT[category] ?? 'neutral';
};

function timeAgo(iso: string): string {
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return '';
  const diff = Math.max(0, Date.now() - ts);
  const min = Math.floor(diff / 60000);
  if (min < 1) return "à l'instant";
  if (min < 60) return `il y a ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `il y a ${h} h`;
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('fr-FR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function compact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace('.', ',')}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1).replace('.', ',')}k`;
  return String(Math.round(n));
}

function quoteCsv(value: string | number): string {
  const s = String(value);
  return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function downloadCsv(report: HomeReport) {
  const rows: Array<Array<string | number>> = [
    ['Indicateur', 'Valeur'],
    ['Généré le', report.generatedAt],
    ["CA aujourd'hui", report.orders.todayRevenue],
    ["Commandes aujourd'hui", report.orders.todayCount],
    ['Panier moyen', report.orders.averageBasket],
    ['Commandes en cours', report.orders.inProgress],
    ['Retards SLA commandes', report.orders.slaLate],
    ['Tickets ouverts', report.support?.open ?? ''],
    ['Tickets urgents', report.support?.urgent ?? ''],
    ['Trésorerie disponible', report.finance?.treasury ?? ''],
    ['Dettes fournisseurs', report.finance?.payables ?? ''],
  ];
  const csv = rows.map((row) => row.map(quoteCsv).join(';')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `reporting-home-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

function seriesForRange(series: HomeRevenuePoint[], range: keyof typeof RANGE_DAYS) {
  return series.slice(-RANGE_DAYS[range]);
}

export function AdminHomePage() {
  const navigate = useNavigate();
  const [range, setRange] = useState<keyof typeof RANGE_DAYS>('14j');
  // La fenêtre demandée est servie par l'endpoint : la série reçue couvre déjà
  // la bonne période, le slice n'est plus qu'un garde-fou (écart 2.1).
  const { data, isLoading, isFetching, isError, refetch } = useReportingHome(RANGE_DAYS[range]);
  const report = data ?? EMPTY_REPORT;
  const revenueSeries = seriesForRange(report.revenueSeries, range);
  const revenueData = revenueSeries.map((p) => p.revenue);
  const revenueLabels = revenueSeries.map((p) =>
    new Date(p.date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }),
  );
  const maxRevenue = Math.max(0, ...revenueData);
  const paymentTotal = report.paymentMix.reduce((sum, item) => sum + item.count, 0);
  const financeAlert = report.finance?.alerts.find((a) => a.level === 'danger' || a.level === 'warn');
  const issueCount = report.issues.length;
  const today = new Date().toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });

  return (
    <>
      <PageHead
        title="Accueil admin"
        sub={`${today} · dernière mise à jour ${formatDateTime(report.generatedAt)}`}
        actions={
          <>
            <AdminButton variant="ghost" onClick={() => downloadCsv(report)} disabled={isLoading || isError}>
              <AdminIcon n="download" s={14} />
              Exporter
            </AdminButton>
            <AdminButton variant="secondary" onClick={() => void refetch()} disabled={isFetching}>
              <AdminIcon n="refresh" s={14} />
              {isFetching ? 'Actualisation…' : 'Actualiser'}
            </AdminButton>
          </>
        }
      />

      {isError && (
        <AlertBanner
          variant="danger"
          title="Accueil indisponible"
          description="Les indicateurs n'ont pas pu être chargés."
          actions={<button onClick={() => void refetch()}>Réessayer</button>}
        />
      )}

      {!isError && issueCount > 0 && (
        <AlertBanner
          variant="info"
          title="Certaines sources sont indisponibles"
          description={`${issueCount} module(s) sans données pour cette vue.`}
        />
      )}

      {!isError && financeAlert && (
        <AlertBanner
          variant={financeAlert.level === 'danger' ? 'danger' : 'warning'}
          title="Alerte finance"
          description={financeAlert.message}
          actions={<button onClick={() => navigate('/finance')}>Voir finance</button>}
        />
      )}

      {/* La couleur du liseré encode l'ÉTAT, pas l'identité de la carte. Elle
          était fixe : le chiffre d'affaires portait un liseré terracotta —
          lu comme une alerte — et les tickets un liseré ocre même à zéro. Une
          couleur d'alerte allumée en permanence ne veut plus rien dire. */}
      <div className="kpi-grid">
        <div className="kpi">
          <div className="kpi-l">CA d&apos;aujourd&apos;hui</div>
          <div className="kpi-v tn">{isLoading ? '…' : fmtFCFA(report.orders.todayRevenue)}</div>
          <span className="kpi-delta neutral">
            {isLoading ? 'Chargement…' : pluriel(report.orders.todayCount, 'commande')}
          </span>
          {revenueData.length > 1 && <AdminSpark data={revenueData} color="var(--a-terracotta)" />}
          <div className="kpi-foot">
            <span>Panier moyen</span>
            <span className="tn">{fmtFCFA(report.orders.averageBasket)}</span>
          </div>
        </div>

        <div className={report.orders.slaLate > 0 ? 'kpi warning' : 'kpi'}>
          <div className="kpi-l">Commandes en cours</div>
          <div className="kpi-v tn">{isLoading ? '…' : report.orders.inProgress}</div>
          <span className={report.orders.slaLate > 0 ? 'kpi-delta down' : 'kpi-delta neutral'}>
            {report.orders.slaLate > 0
              ? `${pluriel(report.orders.slaLate, 'retard')} de livraison`
              : 'Aucun retard signalé'}
          </span>
          <div className="kpi-foot">
            <span>Payées</span>
            <span className="tn">{report.orders.byStatus.PAID ?? 0}</span>
          </div>
        </div>

        <div className={report.support && report.support.slaBreached > 0 ? 'kpi warning' : 'kpi'}>
          <div className="kpi-l">Tickets ouverts</div>
          <div className="kpi-v tn">
            {isLoading ? '…' : (report.support?.open ?? '—')}
            {report.support && report.support.urgent > 0 && (
              <small> · {pluriel(report.support.urgent, 'urgent')}</small>
            )}
          </div>
          <span className={report.support?.slaBreached ? 'kpi-delta down' : 'kpi-delta neutral'}>
            {report.support
              ? report.support.slaBreached > 0
                ? `${pluriel(report.support.slaBreached, 'délai')} ${accord(report.support.slaBreached, 'dépassé')}`
                : 'Tous les délais tenus'
              : 'Support indisponible'}
          </span>
          <div className="kpi-foot">
            <span>Non assignés</span>
            <span className="tn">{report.support?.unassigned ?? '—'}</span>
          </div>
        </div>

        <div className={report.finance && report.finance.overdueCount > 0 ? 'kpi warning' : 'kpi'}>
          <div className="kpi-l">Trésorerie disponible</div>
          <div className="kpi-v tn">{isLoading ? '…' : report.finance ? fmtFCFA(report.finance.treasury) : '—'}</div>
          <span className={report.finance?.overdueCount ? 'kpi-delta down' : 'kpi-delta neutral'}>
            {report.finance
              ? report.finance.overdueCount > 0
                ? `${pluriel(report.finance.overdueCount, 'facture')} en retard`
                : 'Aucune facture en retard'
              : 'Finance indisponible'}
          </span>
          <div className="kpi-foot">
            <span>Dettes fournisseurs</span>
            <span className="tn">{report.finance ? fmtFCFA(report.finance.payables) : '—'}</span>
          </div>
        </div>
      </div>

      <div className="grid-2">
        <div className="stack">
          <Card>
            <CardHead
              title={`Chiffre d'affaires — ${range}`}
              sub="Cumul brut fourni par le reporting"
              right={
                <Segmented
                  value={range}
                  onChange={setRange}
                  options={[
                    { value: '7j', label: '7j' },
                    { value: '14j', label: '14j' },
                    { value: '30j', label: '30j' },
                    { value: '90j', label: '90j' },
                  ]}
                />
              }
            />
            <CardBody>
              {isLoading ? (
                <div className="empty">Chargement…</div>
              ) : revenueData.length > 1 ? (
                <AdminLineChart
                  data={revenueData}
                  labels={revenueLabels}
                  yLabels={['0', compact(maxRevenue / 2), compact(maxRevenue)]}
                  color="var(--a-terracotta)"
                />
              ) : (
                <div className="empty">
                  Aucune vente sur la période.
                  <small>La courbe se trace dès la première commande encaissée.</small>
                </div>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHead
              title="Dernières commandes"
              sub={pluriel(report.latestOrders.length, 'commande')}
              right={
                <AdminButton variant="ghost" size="sm" onClick={() => navigate('/commandes')}>
                  Voir tout <AdminIcon n="chevR" s={12} />
                </AdminButton>
              }
            />
            <div className="table-wrap" style={{ margin: 0 }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Commande</th>
                    <th>Client</th>
                    <th>Ville</th>
                    <th className="right">Total</th>
                    <th>Statut</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading && (
                    <tr>
                      <td colSpan={6} className="muted" style={{ textAlign: 'center', padding: 24 }}>
                        Chargement…
                      </td>
                    </tr>
                  )}
                  {!isLoading && report.latestOrders.length === 0 && (
                    <tr>
                      <td colSpan={6} className="muted" style={{ textAlign: 'center', padding: 24 }}>
                        Aucune commande pour l&apos;instant.
                        <small>Les dernières commandes passées sur la boutique s&apos;afficheront ici.</small>
                      </td>
                    </tr>
                  )}
                  {!isLoading &&
                    report.latestOrders.slice(0, 6).map((order) => {
                      const status = statusOrderV2(order.status);
                      return (
                        <tr key={order.id}>
                          <td>
                            <span className="mono">{order.number}</span>
                            <div className="muted" style={{ fontSize: 11 }}>
                              {formatDateTime(order.createdAt)}
                            </div>
                          </td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <AdminAvatar name={order.customerLabel} />
                              {order.customerLabel}
                            </div>
                          </td>
                          <td>
                            {order.city ? (
                              <>
                                <AdminIcon n="pin" s={12} c="var(--a-ink-mute)" /> {order.city}
                              </>
                            ) : (
                              <span className="muted">—</span>
                            )}
                          </td>
                          <td className="right tn">
                            <strong>{fmtFCFA(order.totalAmount)}</strong>
                            <div className="muted" style={{ fontSize: 11 }}>
                              {order.paymentMethod ?? order.paymentStatus}
                            </div>
                          </td>
                          <td>
                            <Tag variant={status.cls}>{status.l}</Tag>
                          </td>
                          <td className="right">
                            <button className="top-btn" onClick={() => navigate(`/commandes/${order.id}`)}>
                              <AdminIcon n="chevR" s={14} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </Card>
        </div>

        <div className="stack">
          <Card>
            <CardHead
              title="Activité en direct"
              sub="Journal d'audit"
              right={
                <Tag variant={isFetching ? 'warning' : 'success'} noDot style={{ paddingLeft: 6 }}>
                  <span
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      background: 'currentColor',
                      animation: 'pulse-akwaba 1.6s infinite',
                    }}
                  />
                  &nbsp;{isFetching ? 'Maj' : 'Live'}
                </Tag>
              }
            />
            <CardBody style={{ paddingTop: 4, paddingBottom: 4 }}>
              {isLoading && <div className="empty">Chargement…</div>}
              {!isLoading && report.activity.length === 0 && (
                <div className="muted" style={{ padding: '12px 0', fontSize: 12 }}>
                  Aucune activité récente.
                </div>
              )}
              {!isLoading &&
                report.activity.map((entry) => (
                  <div className="feed-item" key={entry.id}>
                    <span
                      className="dot"
                      style={{ background: accentToColor(auditAccent(entry.category, entry.result)) }}
                    />
                    <div className="body">
                      <div>
                        <strong>{entry.actorLabel}</strong>{' '}
                        <span className="muted">
                          {entry.action}
                          {entry.target ? ` · ${String(entry.target).slice(0, 24)}` : ''}
                          {entry.result === 'failure' ? ' (échec)' : ''}
                        </span>
                      </div>
                      <div className="ts">{timeAgo(entry.at)}</div>
                    </div>
                  </div>
                ))}
            </CardBody>
          </Card>

          <Card>
            <CardHead title="Mix paiement" sub="Commandes du jour" />
            <CardBody style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              {paymentTotal > 0 ? (
                <>
                  <AdminDonut
                    total={paymentTotal}
                    slices={report.paymentMix.map((item) => ({
                      color: item.color ?? 'var(--a-border-strong)',
                      value: item.count,
                    }))}
                  />
                  <div style={{ flex: 1, fontSize: 12 }}>
                    {report.paymentMix.map((item) => {
                      const pct = Math.round((item.count / paymentTotal) * 1000) / 10;
                      return (
                        <div
                          key={item.method}
                          style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}
                        >
                          <span>
                            <span
                              style={{
                                display: 'inline-block',
                                width: 8,
                                height: 8,
                                borderRadius: 2,
                                background: item.color ?? 'var(--a-border-strong)',
                                marginRight: 6,
                              }}
                            />
                            {item.label}
                          </span>
                          <strong className="tn">{String(pct).replace('.', ',')}%</strong>
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : (
                <div className="empty" style={{ flex: 1 }}>
                  Aucun encaissement aujourd&apos;hui.
                  <small>La répartition entre MoMo, Orange Money et espèces apparaîtra ici.</small>
                </div>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHead
              title="Support et finance"
              right={
                <AdminButton variant="ghost" size="sm" onClick={() => navigate('/support')}>
                  Support <AdminIcon n="chevR" s={12} />
                </AdminButton>
              }
            />
            <CardBody>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '8px 0',
                  borderBottom: '1px solid var(--a-border)',
                }}
              >
                <div>
                  <strong>Tickets support</strong>
                  <div className="muted" style={{ fontSize: 11 }}>
                    {report.support ? `${report.support.pending} en attente · ${report.support.resolved} résolus` : 'Indisponible'}
                  </div>
                </div>
                <div className="right">
                  <div className="tn">
                    <strong>{report.support?.open ?? '—'}</strong> ouverts
                  </div>
                  <div className="muted" style={{ fontSize: 11 }}>
                    {report.support ? pluriel(report.support.urgent, 'urgent') : '—'}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
                <div>
                  <strong>Finance</strong>
                  <div className="muted" style={{ fontSize: 11 }}>
                    {report.finance ? `${fmtFCFA(report.finance.receivables)} créances` : 'Indisponible'}
                  </div>
                </div>
                <div className="right">
                  <div className="tn">
                    <strong>{report.finance ? fmtFCFA(report.finance.overdueAmount) : '—'}</strong>
                  </div>
                  <div className="muted" style={{ fontSize: 11 }}>
                    en retard
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
    </>
  );
}
